/**
 * Governed reversals — the client side of migrations 738 and 746.
 *
 * Sixteen acts in this product could not be taken back by anyone: a Track bound
 * to the wrong Cycle, a Ball tossed to the wrong division, a finding accepted by
 * a mis-click, a session published a day early. Migrations 738 + 746 give each one a
 * transactional SECURITY DEFINER RPC (build contract §2.3a); this module is the
 * only typed way to call them.
 *
 * Every RPC follows the same contract, so every wrapper here does too:
 *
 *   - a non-empty written reason is mandatory (canon 27 §6 rule 2 —
 *     "revisiting costs at least what deciding cost")
 *   - the reversal is refused once something downstream has consumed the thing,
 *     and the refusal names the consumer in `blockedBy` with a `remedy`
 *   - authority is the doer or the governing authority above them
 *
 * The DB never throws for a business refusal — it returns
 * `{ success: false, error, blocked_by?, remedy? }` — so callers branch on the
 * shape rather than parsing an error string. `ok === false` with a `blockedBy`
 * means "this cannot be undone, do the remedy instead"; `ok === false` without
 * one means "you may not, or there is nothing to undo".
 *
 * Reversals that deliberately do NOT exist (see the migration header): accepting
 * a proposal, closing a loop, ratifying/authorizing a POR and authorizing a
 * reorganization. Their remedy is supersession. (Unrevealing group results was
 * on this list until the 2026-09-22 owner ruling — migration 750 added
 * reopen_group_decision.)
 */

import { getTypedSupabaseClient } from '../typedSupabaseClient';

/** What every reversal returns to the UI. */
export interface ReversalResult {
  /** True only when the reversal actually happened. */
  ok: boolean;
  /** Human-readable refusal, absent on success. */
  error?: string;
  /**
   * The consumer that makes this irreversible (a table or column name), present
   * only when the refusal is a consumption block rather than an authority or
   * state refusal.
   */
  blockedBy?: string;
  /** What to do instead, when the DB offered one. */
  remedy?: string;
  /** The rest of the RPC's payload (ids, counts, resulting state). */
  data?: Record<string, unknown>;
}

const RESERVED_KEYS = new Set(['success', 'error', 'blocked_by', 'remedy']);

/**
 * Map one RPC envelope to a ReversalResult.
 *
 * Exported for the unit test: the mapping is the whole risk surface on this
 * side, because a refusal that reads as a success is exactly the false-success
 * bug class this module exists to avoid.
 */
export function mapReversalResult(
  payload: unknown,
  rpcError?: { message?: string } | null,
): ReversalResult {
  if (rpcError) {
    return { ok: false, error: rpcError.message || 'The reversal failed.' };
  }
  if (payload === null || payload === undefined || typeof payload !== 'object' || Array.isArray(payload)) {
    return { ok: false, error: 'The reversal returned no result.' };
  }

  const row = payload as Record<string, unknown>;
  // Only a literal true counts. A missing or non-boolean `success` is a refusal,
  // never an optimistic pass.
  const ok = row.success === true;

  const data: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(row)) {
    if (!RESERVED_KEYS.has(key)) data[key] = value;
  }

  const result: ReversalResult = { ok };
  if (!ok) {
    result.error = typeof row.error === 'string' && row.error.trim()
      ? row.error
      : 'The reversal was refused.';
  }
  if (typeof row.blocked_by === 'string' && row.blocked_by) result.blockedBy = row.blocked_by;
  if (typeof row.remedy === 'string' && row.remedy) result.remedy = row.remedy;
  if (Object.keys(data).length > 0) result.data = data;
  return result;
}

/** Refuse an empty reason here too, so the round trip is not spent learning it. */
function missingReason(): ReversalResult {
  return { ok: false, error: 'A written reason is required to reverse this (canon 27 §6).' };
}

function hasReason(reason: string | null | undefined): reason is string {
  return typeof reason === 'string' && reason.trim().length > 0;
}

/**
 * The shared shape of a call: guard the reason, then map whatever comes back.
 * Each wrapper passes its own `supabase.rpc(...)` thunk so the generated Args
 * type is checked at the call site — a renamed or re-signatured RPC is a
 * compile error here, not a runtime PGRST202.
 */
type RpcEnvelope = { data: unknown; error: { message?: string } | null };

async function callReversal(
  reason: string | null | undefined,
  call: (trimmedReason: string) => PromiseLike<RpcEnvelope>,
): Promise<ReversalResult> {
  if (!hasReason(reason)) return missingReason();
  const { data, error } = await call(reason.trim());
  return mapReversalResult(data, error);
}

// ── Track and Session filing (canon 28) ─────────────────────────────────────

/** Unbind a Track from its Cycle. Blocked by loops, proposals, locks, official results. */
export function unbindTrackFromCycle(trackId: string, reason: string): Promise<ReversalResult> {
  return callReversal(reason, (p_reason) =>
    getTypedSupabaseClient().rpc('unbind_track_from_cycle', { p_track_id: trackId, p_reason }));
}

/** Unlink a Session from its Track. Blocked once the session has run or produced results. */
export function unlinkSessionFromTrack(sessionId: string, reason: string): Promise<ReversalResult> {
  return callReversal(reason, (p_reason) =>
    getTypedSupabaseClient().rpc('unlink_session_from_track', { p_session_id: sessionId, p_reason }));
}

/** Leave (or remove someone from) a Session. Blocked once they have voiced judgment. */
export function leaveSession(sessionId: string, userId: string, reason: string): Promise<ReversalResult> {
  return callReversal(reason, (p_reason) =>
    getTypedSupabaseClient().rpc('leave_session', { p_session_id: sessionId, p_user_id: userId, p_reason }));
}

// ── Agent inbox triage (canon 20/22, 27 §7.6) ───────────────────────────────

/** Return an accepted/dismissed agent finding to the inbox. Blocked by a citing Decision. */
export function reopenAgentFinding(findingId: string, reason: string): Promise<ReversalResult> {
  return callReversal(reason, (p_reason) =>
    getTypedSupabaseClient().rpc('reopen_agent_finding', { p_finding_id: findingId, p_reason }));
}

/** Return a reviewed nexus proposal to the inbox. Blocked once the edge has been drawn. */
export function reopenNexusProposal(proposalId: string, reason: string): Promise<ReversalResult> {
  return callReversal(reason, (p_reason) =>
    getTypedSupabaseClient().rpc('reopen_nexus_proposal', { p_proposal_id: proposalId, p_reason }));
}

/** Return a reviewed twins cluster to the inbox. Blocked once its items have been merged. */
export function reopenTwinsCluster(clusterId: string, reason: string): Promise<ReversalResult> {
  return callReversal(reason, (p_reason) =>
    getTypedSupabaseClient().rpc('reopen_twins_cluster', { p_cluster_id: clusterId, p_reason }));
}

/** Return a confirmed/dismissed Signal to monitoring. Blocked by a response or a verdict. */
export function reopenSignalEvent(signalId: string, reason: string): Promise<ReversalResult> {
  return callReversal(reason, (p_reason) =>
    getTypedSupabaseClient().rpc('reopen_signal_event', { p_signal_id: signalId, p_reason }));
}

// ── Catchball custody (canon 29) ────────────────────────────────────────────

/** Recall one tossed/passed Ball nobody has opened, claimed, split or answered. */
export function recallBall(ballId: string, reason: string): Promise<ReversalResult> {
  return callReversal(reason, (p_reason) =>
    getTypedSupabaseClient().rpc('recall_ball', { p_ball_id: ballId, p_reason }));
}

/** Recall a whole Rollout — every child Ball of one Split, all-or-nothing. */
export function recallChildBalls(parentBallId: string, reason: string): Promise<ReversalResult> {
  return callReversal(reason, (p_reason) =>
    getTypedSupabaseClient().rpc('recall_child_balls', { p_parent_ball_id: parentBallId, p_reason }));
}

/** Withdraw a Proposal before anybody has decided, countered or rolled it up. */
export function withdrawCatchballProposal(proposalId: string, reason: string): Promise<ReversalResult> {
  return callReversal(reason, (p_reason) =>
    getTypedSupabaseClient().rpc('withdraw_catchball_proposal', { p_proposal_id: proposalId, p_reason }));
}

// ── Publication and planning (canon 04, 104) ────────────────────────────────

/** PUBLISHED → RUN_COMPLETE, unpublishing results and voiding session reports. */
export function retractSessionPublication(sessionId: string, reason: string): Promise<ReversalResult> {
  return callReversal(reason, (p_reason) =>
    getTypedSupabaseClient().rpc('retract_session_publication', { p_session_id: sessionId, p_reason }));
}

/** Take an item back out of a plan period, restoring the placement it replaced. */
export function unplacePlanItem(
  planCycleId: string,
  itemId: string,
  reason: string,
): Promise<ReversalResult> {
  return callReversal(reason, (p_reason) =>
    getTypedSupabaseClient().rpc('unplace_plan_item', {
      p_plan_cycle_id: planCycleId,
      p_item_id: itemId,
      p_reason,
    }));
}

/**
 * Go back to an earlier engine run's results (migration 769): the logged run
 * becomes the active result for its scenario + scope again. Nothing is
 * committed — a run changes nothing until "Propose". Refused
 * with `blockedBy` when the run was logged before full results were kept.
 */
export function restorePlanningRun(historyId: string, reason: string): Promise<ReversalResult> {
  return callReversal(reason, (p_reason) =>
    getTypedSupabaseClient().rpc('restore_planning_run', { p_history_id: historyId, p_reason }));
}

/** Take a Cycle back off the shelf. */
export function unarchiveCycle(cycleId: string, reason: string): Promise<ReversalResult> {
  return callReversal(reason, (p_reason) =>
    getTypedSupabaseClient().rpc('unarchive_cycle', { p_cycle_id: cycleId, p_reason }));
}

// ── Membership and structure (canon 19, 30) ─────────────────────────────────

/** Revoke an unused space invitation by expiring its token. */
export function revokeSpaceInvitation(invitationId: string, reason: string): Promise<ReversalResult> {
  return callReversal(reason, (p_reason) =>
    getTypedSupabaseClient().rpc('revoke_space_invitation', { p_invitation_id: invitationId, p_reason }));
}

/**
 * Revoke a pending workspace invitation (migration 748). Reason required; the
 * inviter or a workspace admin. Refused once accepted (remove the member
 * instead) or already lapsed. The row is kept — expires_at is set to now().
 */
export function revokeWorkspaceInvitation(invitationId: string, reason: string): Promise<ReversalResult> {
  return callReversal(reason, (p_reason) =>
    getTypedSupabaseClient().rpc('revoke_workspace_invitation', { p_invitation_id: invitationId, p_reason }));
}

/** Reverse an org unit retirement (not a reorganization — that supersedes). */
export function restoreOrgUnit(orgUnitId: string, reason: string): Promise<ReversalResult> {
  return callReversal(reason, (p_reason) =>
    getTypedSupabaseClient().rpc('restore_org_unit', { p_org_unit_id: orgUnitId, p_reason }));
}

/** Take back the human approval of an AI-authored item, returning it to ai_draft. */
export function unreviewAiItem(itemId: string, reason: string): Promise<ReversalResult> {
  return callReversal(reason, (p_reason) =>
    getTypedSupabaseClient().rpc('unreview_ai_item', { p_item_id: itemId, p_reason }));
}

// ── Round 2 (migration 746) ─────────────────────────────────────────────────
//
// Not here, on purpose (746 header): unpublishing a pool (publication is a
// memorialized Decision — republish supersedes it instead), discarding a plan
// version (canon 103/104: plan versions are never updated or deleted), and
// deleting a deliberation note (it is retracted and kept).

/** Undo "add item": retire an item nothing uses yet (canon 12 `retire`). */
export function retireNewItem(itemId: string, reason: string): Promise<ReversalResult> {
  return callReversal(reason, (p_reason) =>
    getTypedSupabaseClient().rpc('retire_new_item', { p_item_id: itemId, p_reason }));
}

/**
 * Take back a retired item (archived → active) — the reverse of
 * `retireNewItem` (migration 752). Only items retired through
 * retire_new_item; the retirer, the creator or a workspace admin.
 */
export function restoreRetiredItem(itemId: string, reason: string): Promise<ReversalResult> {
  return callReversal(reason, (p_reason) =>
    getTypedSupabaseClient().rpc('restore_retired_item', { p_item_id: itemId, p_reason }));
}

// ── Migration 758 ───────────────────────────────────────────────────────────

/**
 * "Delete session" for users: retire it (canon 11 §2.1 Tier 1). The row and
 * everything under it stay; lists hide it. Refused (blockedBy) once it is
 * published, live, proposed, lifted, decided, a track's official result,
 * rolled into a loop, or the parent of a follow-on session.
 */
export function retireSession(sessionId: string, reason: string): Promise<ReversalResult> {
  return callReversal(reason, (p_reason) =>
    getTypedSupabaseClient().rpc('retire_session', { p_session_id: sessionId, p_reason }));
}

/** Take back a retired session. The retirer, facilitator, creator or a workspace admin. */
export function restoreRetiredSession(sessionId: string, reason: string): Promise<ReversalResult> {
  return callReversal(reason, (p_reason) =>
    getTypedSupabaseClient().rpc('restore_retired_session', { p_session_id: sessionId, p_reason }));
}

/**
 * Undo "create list": retire an item pool nothing uses yet (status → archived).
 * Refused once locked, published, moved on through catchball, or used by a
 * session, decision, report, cycle, plan or another list.
 */
export function retireItemPool(poolId: string, reason: string): Promise<ReversalResult> {
  return callReversal(reason, (p_reason) =>
    getTypedSupabaseClient().rpc('retire_item_pool', { p_pool_id: poolId, p_reason }));
}

/** Take back a retired list — it returns in the status it had (draft/active). */
export function restoreRetiredItemPool(poolId: string, reason: string): Promise<ReversalResult> {
  return callReversal(reason, (p_reason) =>
    getTypedSupabaseClient().rpc('restore_retired_item_pool', { p_pool_id: poolId, p_reason }));
}

/**
 * Publish a changed list again as a new publication version. Supersedes the
 * previous publication's Decision; the rationale is mandatory.
 */
export function republishItemPool(poolId: string, rationale: string): Promise<ReversalResult> {
  return callReversal(rationale, (p_rationale) =>
    getTypedSupabaseClient().rpc('republish_item_pool', { p_pool_id: poolId, p_rationale }));
}

/** Retract your own deliberation note. The note is kept, hidden, and can't be edited again. */
export function retractDeliberationNote(annotationId: string, reason: string): Promise<ReversalResult> {
  return callReversal(reason, (p_reason) =>
    getTypedSupabaseClient().rpc('retract_deliberation_note', { p_annotation_id: annotationId, p_reason }));
}

/** Cancel a loop that has not been sent (no ball, no proposal, no turns). */
export function cancelCatchballLoop(loopId: string, reason: string): Promise<ReversalResult> {
  return callReversal(reason, (p_reason) =>
    getTypedSupabaseClient().rpc('cancel_catchball_loop', { p_loop_id: loopId, p_reason }));
}

/** Take back a launch's kickoff loops so the cycle's design can be reopened. */
export function reopenCycleDesign(cycleId: string, reason: string): Promise<ReversalResult> {
  return callReversal(reason, (p_reason) =>
    getTypedSupabaseClient().rpc('reopen_cycle_design', { p_cycle_id: cycleId, p_reason }));
}

/** Revert a structure to an earlier version, recorded as a new version. Admins only. */
export function revertStructureToRevision(
  workspaceId: string,
  kind: 'org' | 'product',
  revisionId: string,
  reason: string,
): Promise<ReversalResult> {
  return callReversal(reason, (p_reason) =>
    getTypedSupabaseClient().rpc('revert_structure_to_revision', {
      p_workspace_id: workspaceId,
      p_kind: kind,
      p_revision_id: revisionId,
      p_reason,
    }));
}

/** Reopen a resolved cluster, restoring the items it closed as duplicates. */
export function reopenResolvedCluster(groupId: string, reason: string): Promise<ReversalResult> {
  return callReversal(reason, (p_reason) =>
    getTypedSupabaseClient().rpc('reopen_resolved_cluster', { p_group_id: groupId, p_reason }));
}

// ── Group decisions (migration 750) ────────────────────────────────────────

/**
 * Step a group decision back one state: DECIDED → REVEALING (the winner is
 * withdrawn) or REVEALING → SCORING (the reveal is withdrawn). Facilitator or
 * workspace admin only; blocked once the result was published, packaged into
 * a proposal, lifted into a priority list, recorded as a decision, reported, or
 * made a track's official result.
 */
export function reopenGroupDecision(sessionId: string, reason: string): Promise<ReversalResult> {
  return callReversal(reason, (p_reason) =>
    getTypedSupabaseClient().rpc('reopen_group_decision', { p_session_id: sessionId, p_reason }));
}

// ── Generate tracks (migration 767, owner ruling #6) ──────────────────────

/**
 * Undo one Generate tracks run (materialize_baseline_tracks): retire the
 * tracks and pools it created, put changed lane lists back exactly as they
 * were, and bring back the tracks it archived. `runId` omitted = the latest
 * run on the cycle not yet undone. Cycle owner or workspace admin. Refused
 * (blockedBy) for a run from before runs were recorded, one already undone,
 * one with a later run still in place, once a created lane was ranked on or
 * bound (session, result, locked pool, loop, proposal, decision, official
 * result), or once a changed list was edited or locked since.
 */
export function undoBaselineTracksRun(cycleId: string, reason: string, runId?: string | null): Promise<ReversalResult> {
  return callReversal(reason, (p_reason) =>
    getTypedSupabaseClient().rpc('undo_baseline_tracks_run', {
      p_cycle_id: cycleId,
      p_reason,
      ...(runId ? { p_run_id: runId } : {}),
    }));
}

/** A recorded Generate tracks run that can still be offered for undo. */
export interface UndoableBaselineRun {
  runId: string;
  occurredAt: string;
  created: number;
  changed: number;
  archived: number;
}

/**
 * The latest Generate tracks run on a cycle that was recorded (767+) and not
 * yet undone, or null. Read-only; governance_events is readable by workspace
 * members. The server re-checks everything when the undo is asked for.
 */
export async function findUndoableBaselineRun(cycleId: string): Promise<UndoableBaselineRun | null> {
  const client = getTypedSupabaseClient();
  const [runs, reversals] = await Promise.all([
    client
      .from('governance_events')
      .select('id, occurred_at, before_state_snapshot')
      .eq('cycle_id', cycleId)
      .eq('event_type', 'BaselineTracksGenerated')
      .order('occurred_at', { ascending: false })
      .limit(10),
    client
      .from('governance_events')
      .select('after_state_snapshot')
      .eq('cycle_id', cycleId)
      .eq('event_type', 'baseline_tracks_run_reversed'),
  ]);
  if (runs.error || reversals.error) return null;
  const undone = new Set(
    (reversals.data ?? [])
      .map((r) => (r.after_state_snapshot as Record<string, unknown> | null)?.run_id)
      .filter((id): id is string => typeof id === 'string'),
  );
  // Only the latest not-yet-undone run can be undone (a later run blocks an earlier one).
  const latest = (runs.data ?? []).find((r) => !undone.has(r.id));
  const undo = (latest?.before_state_snapshot as Record<string, unknown> | null)?.undo as
    | Record<string, unknown>
    | undefined;
  if (!latest || !undo || typeof undo !== 'object') return null;
  const count = (key: string) => (Array.isArray(undo[key]) ? (undo[key] as unknown[]).length : 0);
  if (count('created') + count('changed') + count('archived') === 0) return null;
  return {
    runId: latest.id,
    occurredAt: latest.occurred_at,
    created: count('created'),
    changed: count('changed'),
    archived: count('archived'),
  };
}


/**
 * Withdraw a scorer's group scores (migration 768 — owner ruling 2026-09-22 #1).
 * Only while the session is SCORING: once results are revealed the room has
 * seen them, so the refusal names `group_session_status` and the remedy is to
 * reopen scoring (reopenGroupDecision) — a revealed result is superseded, not
 * undone. The scorer themselves, or the facilitator / a workspace admin for
 * someone else (pass `target`). Omit `target` to withdraw your own.
 */
export function withdrawGroupScores(
  sessionId: string,
  reason: string,
  target?: { userId?: string; guestParticipantId?: string },
): Promise<ReversalResult> {
  return callReversal(reason, (p_reason) =>
    getTypedSupabaseClient().rpc('withdraw_group_scores', {
      p_session_id: sessionId,
      p_reason,
      p_user_id: target?.userId || undefined,
      p_guest_participant_id: target?.guestParticipantId || undefined,
    }));
}

// ── Personal inbox (not governance — no reason, no event) ───────────────────

/** Mark one of your notifications unread again. False when it isn't yours. */
export async function markNotificationUnread(notificationId: string): Promise<boolean> {
  const { data, error } = await getTypedSupabaseClient().rpc('mark_notification_unread', {
    p_notification_id: notificationId,
  });
  return !error && data === true;
}

/** Bring back a notification you dismissed. False when it isn't yours. */
export async function undismissNotification(notificationId: string): Promise<boolean> {
  const { data, error } = await getTypedSupabaseClient().rpc('undismiss_notification', {
    p_notification_id: notificationId,
  });
  return !error && data === true;
}
